import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { LocaleContext } from "../../routing/LangRouter";

//import constants
import { ItineraryContext } from "../../constants/layouts/itinerary-layout";
import BASE_URL from "../../constants/baseUrls";

//import general components
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import { BaseButton } from "../../components/common/base-button/base-button";

//import view components
import Questions from "../../components/view/favorites/questions/questions";
import FavoritesHeader from "../../components/view/favorites/favorites-header/favorites-header";

//import style
import "./favorites.scss";

function Favorites() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const [loading, setLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isTicket, isAccommodation, isFlightInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    useEffect(() => {
        if (!itineraryInformation) handleGoHome();
    }, []);

    //state for storing user's favorite answers
    const [answers, setAnswers] = useState({});

    //handler for updating answers state
    const createAnswerObject = newAnswers => {
        setAnswers(newAnswers);
    };

    //handle form submission and navigation
    const submitAnswers = async (e, finalAnswers) => {
        e.preventDefault();
        updateItineraryInformation();

        const hasMinimumAnswers = countValidAnswers(finalAnswers) > 7;
        if (!hasMinimumAnswers) {
            toast.error(t("favoritesMessage"), { toastId: "custom-id-yes" });
            return;
        }

        //save answers and update user profile if logged in
        sessionStorage.setItem("favorites", JSON.stringify(finalAnswers));

        if (accessToken) {
            await editFavorites(finalAnswers);
        }

        //determine next route based on ticket/accommodation preferences
        if (isTicket && !isFlightInternational) {
            setRoutesInStorages([`/${locale}/itinerary/domestic-flight`]);
            navigateNextLevel("domestic-flight");
        } else if (isTicket && !!isFlightInternational) {
            setRoutesInStorages([`/${locale}/itinerary/international-flight`]);
            navigateNextLevel("international-flight");
        } else if (isAccommodation && !isFlightInternational) {
            setRoutesInStorages([`/${locale}/itinerary/domestic-flight`, `/${locale}/itinerary/hotel`]);
            navigateNextLevel("hotel");
        } else if (isAccommodation && !!isFlightInternational) {
            setRoutesInStorages([`/${locale}/itinerary/international-flight`, `/${locale}/itinerary/hotel`]);
            navigateNextLevel("hotel");
        } else {
            setRoutesInStorages([`/${locale}/itinerary/itinerary-page`]);
            navigateNextLevel("itinerary-page");
        }
    };

    //submit favorites to user profile if logged in
    const editFavorites = async newAnswers => {
        setLoading(true)
        await axios.patch(`${BASE_URL}/account/user/favorites/`, newAnswers, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
        });
        setLoading(false)
    };

    //count valid answers
    const countValidAnswers = (answers) => {
        return Object.values(answers).reduce((count, value) => (value > 0 ? count + 1 : count), 0);
    };

    //update itinerary generation flag
    const updateItineraryInformation = () => {
        if (!itineraryInformation) return;
        itineraryInformation.itineraryGenerate = true;
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    //set routes in session storage
    const setRoutesInStorages = routeList => {
        const routesInSession = JSON.parse(sessionStorage.getItem("visitedRoutes")) || [];
        routeList.forEach(route => {
            if (!routes.includes(route)) {
                setRoutes([...routes, route]);
            }
            if (!routesInSession.includes(route)) {
                sessionStorage.setItem("visitedRoutes", JSON.stringify([...routesInSession, route]));
            }
        });
    };

    //navigate to the next level
    const navigateNextLevel = nextRoute => {
        navigate(`/${locale}/itinerary/${nextRoute}`);
    };

    //go to home page
    const handleGoHome = () => {
        navigate(`/${locale}`, { replace: true });
    };

    return (
        <Grid className="favorites" container item xs={12} mb={5}>
            <MobileHeader title={t("favorites")} justifyContent="center" isButton={false} handleClick={handleGoHome} />

            <FavoritesHeader submitAnswers={submitAnswers} />

            <Container maxWidth="xl">
                <Grid container justifyContent="center" rowGap={5} mt={4}>
                    <Questions totalAnswers={createAnswerObject} />

                    <Grid container item xs={12} sm={8} md={6} lg={4} justifyContent="center">
                        <BaseButton
                            text={t("letsGo")}
                            color="primary"
                            size="large"
                            loading={loading}
                            radius="radius-10"
                            handleClick={e => submitAnswers(e, answers)}
                        />
                    </Grid>
                </Grid>
            </Container>
        </Grid>
    );
}

export default Favorites;
